import { FormDataConfiguration } from "types/Models/task";
import {
  AutofillDetailKey,
  FIELD_TYPE,
  Section as FilingSection,
  SectionField,
} from "types/Models/Filing";
import { array, mixed, object, string } from "yup";
import { hasUploadOrLinkValue } from "util/booksHandoff/hasUploadOrLinkValue";
import { isUploadOrLinkRequired } from "util/booksHandoff/isUploadOrLinkRequired";
import { isDelawareRegisteredAgentAddress } from "components/fileTax/DelawareRegisteredAgentAddresses";

export const DELAWARE_AGENT_ADDRESS_ERROR =
  "You cannot use your Delaware Registered Agent address. Doing so may lead to rejection by the state authority.";

const AUTOFILL_KEY_TO_DETAIL_KEY: Record<string, AutofillDetailKey> = {
  addresses: "address_details",
  bank_details: "bank_details",
  persona_details: "persona_details",
};

const NO_ADDED_REPEATABLE_IDS: Record<string, number> = {};

const withRepeatId = (fieldKey: string, id: number) =>
  `${fieldKey.split("_").slice(0, -1).join("_")}_${id}`;

// Saved repeatable rows plus rows added on screen but not saved yet (ids above the saved max)
const getRepeatableFields = (
  section: FilingSection,
  addedMaxId = 0
): SectionField[] => {
  const repeatables = section.repeatable_fields ?? [];

  if (repeatables.length === 0) {
    return [];
  }

  const savedMaxId = Math.max(...repeatables.map(({ id }) => id));
  const addedIds = Array.from(
    { length: Math.max(addedMaxId - savedMaxId, 0) },
    (_, index) => savedMaxId + index + 1
  );
  const addedFields = addedIds.flatMap((id) =>
    (repeatables[0].fields ?? []).map((field) => ({
      ...field,
      field_key: withRepeatId(field.field_key, id),
    }))
  );

  return [...repeatables.flatMap(({ fields }) => fields ?? []), ...addedFields];
};

// Only enforced while the field is rendered, so hidden or removed fields are skipped
const getOnScreenFieldSchema = (field: SectionField) => {
  const isOnScreen = () =>
    Boolean(document.querySelector(`[name="${field.field_key}"]`));

  if (field.type === "email") {
    return string().when({
      is: isOnScreen,
      then: string().nullable().trim().required("This field is required"),
      otherwise: string().nullable().notRequired(),
    });
  }

  if (["file_upload", "multiselect"].includes(field.type)) {
    return array().when({
      is: isOnScreen,
      then: array().min(1, "This field is required"),
      otherwise: array().notRequired(),
    });
  }

  return string().when({
    is: isOnScreen,
    then: string().nullable().trim().required("This field is required"),
    otherwise: string().nullable().notRequired(),
  });
};

export const getValidationSchema = (
  formData: FormDataConfiguration,
  {
    isCompletingTask = false,
    isCustomer = false,
    addedRepeatableIds = NO_ADDED_REPEATABLE_IDS,
  }: {
    isCompletingTask?: boolean;
    isCustomer?: boolean;
    addedRepeatableIds?: Record<string, number>;
  } = {}
) => {
  let shape: Record<string, any> = {};

  formData?.data?.data?.section_group?.forEach((group) => {
    group.sections?.forEach((sectionData) => {
      const section = sectionData as FilingSection;

      if (section.required_on_completion && !isCompletingTask) {
        return;
      }

      section.section_fields?.forEach((field) => {
        if (!field.required) {
          return;
        }

        if (
          (field.type === "select_autofill" ||
            field.type === "select_principal_officer_autofill" ||
            field.type === "select_authorized_signatory_autofill" ||
            field.type === "select_bank_autofill") &&
          field.field_key
        ) {
          const lookupKey = field.validation_key || field.autofill_key;
          const detailKey = lookupKey
            ? AUTOFILL_KEY_TO_DETAIL_KEY[lookupKey]
            : undefined;

          if (detailKey && field.field_key) {
            const radioKeys = Object.keys(field.radio || {});

            let fieldSchema = mixed().when(radioKeys, {
              is: (...radioVals: string[]) => {
                const isHidden =
                  radioKeys.length > 0 &&
                  !radioKeys.some(
                    (key, i) => radioVals[i] === field.radio![key]
                  );

                if (isHidden) {
                  return false;
                }

                const details = section.selected_autofill_details?.[detailKey];
                return !details?.uuid;
              },
              then: mixed().required(
                field.error_message || "This field is required"
              ),
              otherwise: mixed().notRequired(),
            });

            if (
              field.field_key === "principle_place_of_business" &&
              detailKey === "address_details"
            ) {
              fieldSchema = fieldSchema.test(
                "not-delaware-registered-agent",
                DELAWARE_AGENT_ADDRESS_ERROR,
                () => {
                  const details =
                    section.selected_autofill_details?.address_details;
                  if (!details?.uuid) {
                    return true;
                  }
                  return !isDelawareRegisteredAgentAddress(details);
                }
              );
            }

            shape[field.field_key] = fieldSchema;
          }
          return;
        }

        if (field.type === "select_directors_autofill" && field.field_key) {
          const radioKeys = Object.keys(field.radio || {});

          shape[field.field_key] = mixed().when(radioKeys, {
            is: (...radioVals: string[]) => {
              const isHidden =
                radioKeys.length > 0 &&
                !radioKeys.some((key, i) => radioVals[i] === field.radio![key]);

              if (isHidden) {
                return false;
              }

              const validationKey = field.validation_key;
              const hasDirectorWithDetails = section.repeatable_fields?.some(
                (rf) =>
                  validationKey &&
                  rf.selected_autofill_details?.[
                    validationKey as keyof typeof rf.selected_autofill_details
                  ]
              );

              return !hasDirectorWithDetails;
            },
            then: mixed().required(
              field.error_message || "This field is required"
            ),
            otherwise: mixed().notRequired(),
          });

          return;
        }

        if (field.type === FIELD_TYPE.UPLOAD_OR_LINK) {
          const fieldKey = field.field_key;
          shape[fieldKey] = mixed().test(
            "upload-or-link-required",
            "Please upload a document or add a link",
            function () {
              return (
                !isUploadOrLinkRequired(field, this.parent, isCustomer) ||
                hasUploadOrLinkValue(fieldKey, this.parent)
              );
            }
          );
          return;
        }

        shape[field.field_key] = getOnScreenFieldSchema(field);
      });

      getRepeatableFields(section, addedRepeatableIds[section.section_key])
        .filter((field) => field.required)
        .forEach((field) => {
          shape[field.field_key] = getOnScreenFieldSchema(field);
        });
    });
  });

  return object().shape(shape);
};
